import { jsxs, jsx } from "react/jsx-runtime";
import { cx, css } from "@/styled-system/css";
import { Box } from "@zillow/constellation-10";
import { omit } from "lodash";
import withPreload from "../../utils/with-preload.esm.js";
import Picture from "../Picture/Picture.esm.js";
import Source from "../Source/Source.esm.js";
import GroupBackgroundImage from "./GroupBackgroundImage.esm.js";
import GroupBackgroundVideo from "./GroupBackgroundVideo.esm.js";
import { v8ResponsiveToPanda } from "../../utils/token-migration/migrate-responsive.esm.js";
import { migrateColorToken, migrateSpacingToken } from "../../utils/token-migration/v8-to-v10.esm.js";
const GroupBackgroundImagePreload = withPreload(GroupBackgroundImage);
const GroupBackgroundVideoPreload = withPreload(GroupBackgroundVideo, {
  as: "video"
});
const TRANSPARENT_PIXEL_SOURCE = {
  src: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
  height: 0,
  width: 0,
  loading: "lazy"
};
const WIDTH_MAP = {
  xs: "[320px]",
  sm: "[480px]",
  md: "[768px]",
  lg: "[1024px]",
  xl: "[1280px]",
  full: "[1920px]"
};
const HEIGHT_MAP = {
  none: "auto",
  short: "[300px]",
  tall: "[500px]",
  fluid: "[100vh]"
};
const pixelMinHeightClass = css({
  minHeight: "[var(--gmh-base)]",
  md: {
    minHeight: "[var(--gmh-md, var(--gmh-base))]"
  },
  xl: {
    minHeight: "[var(--gmh-xl, var(--gmh-md, var(--gmh-base)))]"
  }
});
const withPxUnit = (v) => {
  const s = (v ?? "").toString().trim();
  if (!s || s === "0") return void 0;
  return /^-?\d+(\.\d+)?$/.test(s) ? `${s}px` : s;
};
function resolvePixelMinHeightVars(minHeight, minHeightPixel) {
  const usesPixel = typeof minHeight === "string" ? minHeight === "pixel" : !!minHeight && typeof minHeight === "object" && Object.values(minHeight).includes("pixel");
  if (!usesPixel) return void 0;
  const pxObj = minHeightPixel && typeof minHeightPixel === "object" ? minHeightPixel : {
    default: minHeightPixel
  };
  const resolved = v8ResponsiveToPanda(pxObj, withPxUnit);
  const vars = {};
  if (resolved.base) vars["--gmh-base"] = resolved.base;
  if (resolved.md) vars["--gmh-md"] = resolved.md;
  if (resolved.xl) vars["--gmh-xl"] = resolved.xl;
  return Object.keys(vars).length ? vars : void 0;
}
const BG_TOKEN_REMAP = {
  "text.accent.brand.hero": "blue.600"
};
function migrateBackgroundColor(v8Color) {
  const v10 = migrateColorToken(v8Color) ?? v8Color;
  return BG_TOKEN_REMAP[v10] ?? v10;
}
const RADIUS_MAP = {
  sm: "obj.default",
  md: "obj.default",
  lg: "obj.soft",
  pill: "shape.capsule",
  ellipse: "50%",
  none: "0",
  // v8 xl=16px / xxl=24px have no semantic obj.* token (obj is 4/12/20px),
  // so map to the primitive radii tokens that match those sizes exactly:
  // radii.400 = 1rem = 16px, radii.600 = 1.5rem = 24px (see AgentLoginModal precedent).
  xl: "400",
  xxl: "600"
};
function resolveSpacingToken(v) {
  if (!v) return void 0;
  if (v === "0") return "none";
  return migrateSpacingToken(v) ?? v;
}
function resolveResponsiveSpacing(value) {
  if (!value) return void 0;
  if (typeof value === "string") return resolveSpacingToken(value);
  const keys = Object.keys(value);
  if (keys.length === 1 && "default" in value) {
    return resolveSpacingToken(value.default);
  }
  return v8ResponsiveToPanda(value, resolveSpacingToken);
}
const baseGroupClass = css({
  position: "relative",
  marginLeft: "auto",
  marginRight: "auto",
  width: "100%",
  flex: "1",
  flexDirection: "column",
  "& > *:not(picture, video, img)": {
    position: "relative"
  }
});
function buildGroupStyles(props) {
  const styles = {};
  const {
    visible
  } = props;
  if (visible && typeof visible === "object") {
    const pandaDisplay = v8ResponsiveToPanda(visible, (v) => v ? "flex" : "none");
    styles.display = pandaDisplay;
  } else {
    styles.display = "flex";
  }
  const alignItems = props.alignItems ?? "center";
  if (typeof alignItems === "string") {
    styles.alignItems = alignItems;
  } else if (alignItems && typeof alignItems === "object") {
    styles.alignItems = v8ResponsiveToPanda(alignItems);
  }
  const justifyContent = props.justifyContent ?? "center";
  if (typeof justifyContent === "string") {
    styles.justifyContent = justifyContent;
  } else if (justifyContent && typeof justifyContent === "object") {
    styles.justifyContent = v8ResponsiveToPanda(justifyContent);
  }
  if (props.backgroundColor) {
    if (typeof props.backgroundColor === "string") {
      styles.backgroundColor = migrateBackgroundColor(props.backgroundColor) ?? props.backgroundColor;
    } else {
      styles.backgroundColor = v8ResponsiveToPanda(props.backgroundColor, (v) => migrateBackgroundColor(v) ?? v);
    }
  }
  if (props.fontColor) {
    if (typeof props.fontColor === "string") {
      const v10Color = migrateColorToken(props.fontColor);
      if (v10Color) styles.color = v10Color;
      const linkColor = props.fontColor === "textWhite" ? "text.onImpact.link.default" : "text.link.default";
      const linkHoverColor = props.fontColor === "textWhite" ? "text.onImpact.link.default" : "text.link.hover";
      styles['& a:not([data-c11n-component="Button"]):not([data-c11n-component="TextButton"])'] = {
        color: linkColor,
        _hover: {
          color: linkHoverColor
        }
      };
    } else {
      const responsiveColor = v8ResponsiveToPanda(props.fontColor, (v) => migrateColorToken(v) ?? v);
      styles.color = responsiveColor;
      const defaultFontColor = props.fontColor.default;
      const linkColor = defaultFontColor === "textWhite" ? "text.onImpact.link.default" : "text.link.default";
      const linkHoverColor = defaultFontColor === "textWhite" ? "text.onImpact.link.default" : "text.link.hover";
      styles['& a:not([data-c11n-component="Button"]):not([data-c11n-component="TextButton"])'] = {
        color: linkColor,
        _hover: {
          color: linkHoverColor
        }
      };
    }
  }
  const maxWidth = props.maxWidth ?? "xl";
  if (typeof maxWidth === "string") {
    styles.maxWidth = WIDTH_MAP[maxWidth] ?? maxWidth;
  } else if (maxWidth && typeof maxWidth === "object") {
    styles.maxWidth = v8ResponsiveToPanda(maxWidth, (v) => WIDTH_MAP[v] ?? v);
  }
  const minHeight = props.minHeight ?? "none";
  if (typeof minHeight === "string") {
    if (minHeight !== "pixel") {
      styles.minHeight = HEIGHT_MAP[minHeight] ?? minHeight;
    }
  } else if (minHeight && typeof minHeight === "object") {
    styles.minHeight = v8ResponsiveToPanda(minHeight, (v) => {
      if (v === "pixel") {
        return void 0;
      }
      return HEIGHT_MAP[v] ?? v;
    });
  }
  if (props.padding) {
    const sides = ["paddingTop", "paddingRight", "paddingBottom", "paddingLeft"];
    for (const side of sides) {
      const val = props.padding[side];
      if (val) {
        const resolved = resolveResponsiveSpacing(val);
        if (resolved) styles[side] = resolved;
      }
    }
  }
  const marginKeys = ["margin", "marginTop", "marginBottom", "marginRight", "marginLeft"];
  for (const key of marginKeys) {
    const val = props[key];
    if (val) {
      const resolved = resolveResponsiveSpacing(val);
      if (resolved) styles[key] = resolved;
    }
  }
  if (props.borderRadius) {
    const br = props.borderRadius;
    const sides = {
      borderRadiusTopLeft: "borderTopLeftRadius",
      borderRadiusTopRight: "borderTopRightRadius",
      borderRadiusBottomRight: "borderBottomRightRadius",
      borderRadiusBottomLeft: "borderBottomLeftRadius"
    };
    let hasRadius = false;
    for (const [v8Key, cssKey] of Object.entries(sides)) {
      const val = br[v8Key];
      if (val !== void 0) {
        hasRadius = true;
        const rawVal = val && typeof val === "object" ? val.default ?? "" : String(val);
        styles[cssKey] = RADIUS_MAP[rawVal] ?? rawVal;
      }
    }
    if (hasRadius) styles.overflow = "hidden";
  }
  return styles;
}
const Group = ({
  children,
  backgroundImage,
  backgroundImageVisible,
  backgroundVideo,
  backgroundVideoVisible,
  backgroundPosition,
  backgroundSize,
  backgroundVideoPosition,
  backgroundVideoSize,
  loading,
  backgroundImagePreloaded,
  isEditor = false,
  ...rest
}) => {
  const GroupBackgroundVideoComponent = isEditor ? GroupBackgroundVideo : GroupBackgroundVideoPreload;
  const groupStyles = buildGroupStyles(rest);
  const {
    role,
    id,
    style
  } = rest;
  const pixelMinHeightVars = resolvePixelMinHeightVars(rest.minHeight, rest.minHeightPixel);
  const mergedStyle = pixelMinHeightVars ? {
    ...pixelMinHeightVars,
    ...style
  } : style;
  return /* @__PURE__ */ jsxs(Box, { className: cx(baseGroupClass, pixelMinHeightVars ? pixelMinHeightClass : void 0, rest.className), css: groupStyles, role, id, style: mergedStyle, children: [
    renderBackgroundImage({
      backgroundImage,
      backgroundImageVisible,
      backgroundPosition,
      backgroundSize,
      loading,
      preloaded: backgroundImagePreloaded
    }),
    /* @__PURE__ */ jsx(GroupBackgroundVideoComponent, { source: backgroundVideo, visible: backgroundVideoVisible, size: backgroundVideoSize, position: backgroundVideoPosition, loading, isEditor }),
    children
  ] });
};
function renderBackgroundImage({
  backgroundImage,
  backgroundImageVisible,
  backgroundPosition,
  backgroundSize,
  loading,
  preloaded
}) {
  var _a;
  const bgImageMap = backgroundImage;
  const visibleMap = backgroundImageVisible;
  const defaultSource = ((_a = bgImageMap == null ? void 0 : bgImageMap.default) == null ? void 0 : _a.src) ? bgImageMap.default : TRANSPARENT_PIXEL_SOURCE;
  const lgLteSource = (visibleMap == null ? void 0 : visibleMap.lg_lte) ? bgImageMap == null ? void 0 : bgImageMap.lg_lte : void 0;
  const smLteSource = (visibleMap == null ? void 0 : visibleMap.sm_lte) ? bgImageMap == null ? void 0 : bgImageMap.sm_lte : void 0;
  const hasBackgroundImage = [defaultSource, lgLteSource, smLteSource].filter(Boolean).some((source) => source !== TRANSPARENT_PIXEL_SOURCE);
  if (!hasBackgroundImage) {
    return null;
  }
  const GroupBackgroundImageComponent = preloaded ? GroupBackgroundImagePreload : GroupBackgroundImage;
  const pictureDisplayClass = visibleMap && typeof visibleMap === "object" ? css({
    display: v8ResponsiveToPanda(visibleMap, (v) => v ? "block!" : "none!")
  }) : void 0;
  return /* @__PURE__ */ jsxs(Picture, { className: pictureDisplayClass, children: [
    smLteSource && /* @__PURE__ */ jsx(Source, { srcSet: smLteSource.src, media: "sm_lte", loading, ...omit(smLteSource, "src") }),
    lgLteSource && /* @__PURE__ */ jsx(Source, { srcSet: lgLteSource.src, media: "lg_lte", loading, ...omit(lgLteSource, "src") }),
    /* @__PURE__ */ jsx(GroupBackgroundImageComponent, { src: defaultSource.src, backgroundPosition, backgroundSize, loading, ...omit(defaultSource, "src") })
  ] });
}
export {
  Group as default
};
